/* eslint-disable */
import React, { useState } from 'react';
import { useAppData } from '../../context/AppDataContext';
import { projectsAPI, calendarAPI, settingsAPI, orgMembersAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { formatDate } from '../../utils/helpers';
import { formatMoney, symbolFor } from '../../utils/currencyTotals';

const STATUS_OPTIONS = [
  { value: 'not-started', label: 'Not Started' },
  { value: 'planning',    label: 'Planning' },
  { value: 'active',      label: 'Active' },
  { value: 'completed',   label: 'Completed' },
  { value: 'archived',    label: 'Archived' }
];

const defaultForm     = { name: '', description: '', status: 'planning', budget: '', startDate: '', endDate: '', propertyId: null, tasks: [] };
const defaultTaskForm = { name: '', description: '', dueDate: '', duration: '', dependsOn: [], completed: false, assignedTo: '' };

export default function Projects() {
  const { projects, setProjects, properties, setCalendarTasks, settings, setSettings } = useAppData();
  const [selectedProject, setSelectedProject] = useState(null);
  const [showAddModal, setShowAddModal]         = useState(false);
  const [editProject, setEditProject]           = useState(null);
  const [showTaskModal, setShowTaskModal]       = useState(false);
  const [editTask, setEditTask]                 = useState(null);
  const [showArchiveView, setShowArchiveView]   = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [loading, setLoading]                   = useState(false);
  const [orgUsers, setOrgUsers]                 = useState([]);
  const [error, setError]                       = useState('');
  const [form, setForm]                         = useState(defaultForm);
  const [taskForm, setTaskForm]                 = useState(defaultTaskForm);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const { user } = useAuth();

  const set  = (f, v) => setForm(p => ({ ...p, [f]: v }));
  const setTF = (f, v) => setTaskForm(p => ({ ...p, [f]: v }));

  React.useEffect(() => {
    orgMembersAPI.getAll()
      .then(members => { if (Array.isArray(members) && members.length > 0) setOrgUsers(members); })
      .catch(() => { if (user) setOrgUsers([{ id: user.id, username: user.username }]); });
  }, []);

  // ─── Templates ────────────────────────────────────────────────────────────

  const templates = (() => {
    try { return JSON.parse(settings?.project_templates || '[]'); } catch { return []; }
  })();

  const saveTemplates = async (newTemplates) => {
    try {
      await settingsAPI.update({ project_templates: JSON.stringify(newTemplates) });
      setSettings(prev => ({ ...(prev || {}), project_templates: JSON.stringify(newTemplates) }));
    } catch (err) { console.warn('Failed to save templates:', err); }
  };

  const saveAsTemplate = async (proj) => {
    const name = window.prompt('Template name:', `${proj.name} Template`);
    if (!name) return;
    const template = {
      id: `tmpl_${Date.now()}`,
      name,
      description: proj.description || '',
      tasks: (proj.tasks || []).map(t => ({ ...t, completed: false, id: `task_${Date.now()}_${Math.random().toString(36).substr(2, 6)}` })),
      createdAt: new Date().toISOString()
    };
    await saveTemplates([...templates, template]);
    alert(`✅ "${name}" saved as template!`);
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;
    await saveTemplates(templates.filter(t => t.id !== templateId));
  };

  const applyTemplate = (templateId) => {
    const tmpl = templates.find(t => t.id === templateId);
    if (!tmpl) return;
    setForm(p => ({
      ...p,
      name: p.name || tmpl.name,
      description: p.description || tmpl.description,
      tasks: tmpl.tasks.map(t => ({ ...t, id: `task_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`, completed: false }))
    }));
    setSelectedTemplate('');
  };

  // ─── Project CRUD ──────────────────────────────────────────────────────────

  const openAdd  = () => { setForm(defaultForm); setEditProject(null); setSelectedTemplate(''); setError(''); setShowAddModal(true); };
  const openEdit = (proj) => {
    setForm({ name: proj.name || '', description: proj.description || '', status: proj.status || 'planning', budget: proj.budget || '', startDate: proj.start_date || proj.startDate || '', endDate: proj.end_date || proj.endDate || '', propertyId: proj.property_id || proj.propertyId || null, tasks: proj.tasks || [] });
    setEditProject(proj); setSelectedTemplate(''); setError(''); setShowAddModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try {
      const data = { name: form.name, description: form.description, status: form.status, budget: form.budget ? parseFloat(form.budget) : null, startDate: form.startDate || null, endDate: form.endDate || null, propertyId: form.propertyId || null, tasks: form.tasks || [] };
      if (editProject) {
        const r = await projectsAPI.update(editProject.id, data);
        setProjects(prev => prev.map(p => p.id === editProject.id ? r : p));
        if (selectedProject?.id === editProject.id) setSelectedProject(r);
      } else {
        const r = await projectsAPI.create(data);
        setProjects(prev => [r, ...prev]);
        setSelectedProject(r);
      }
      setShowAddModal(false);
    } catch (err) { setError(err.error || 'Failed to save project.'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    try { await projectsAPI.delete(id); setProjects(prev => prev.filter(p => p.id !== id)); if (selectedProject?.id === id) setSelectedProject(null); }
    catch (err) { alert(err.error || 'Failed'); }
  };

  const handleArchiveToggle = async (proj) => {
    const newStatus = proj.status === 'archived' ? 'planning' : 'archived';
    try {
      const r = await projectsAPI.update(proj.id, { ...proj, name: proj.name, status: newStatus, startDate: proj.start_date || proj.startDate, endDate: proj.end_date || proj.endDate, propertyId: proj.property_id || proj.propertyId, tasks: proj.tasks || [] });
      setProjects(prev => prev.map(p => p.id === proj.id ? r : p));
      if (selectedProject?.id === proj.id) setSelectedProject(null);
    } catch (err) { alert(err.error || 'Failed'); }
  };

  const liveProj = () => projects.find(p => p.id === selectedProject?.id) || selectedProject;

  // ─── Task CRUD ─────────────────────────────────────────────────────────────

  const saveProjectTasks = async (updatedTasks) => {
    const proj = liveProj();
    if (!proj) { alert('Please save the project first before adding tasks.'); return null; }
    setLoading(true);
    try {
      const totalTasks     = updatedTasks.length;
      const completedCount = updatedTasks.filter(t => t.completed).length;
      let newStatus = proj.status;
      if (totalTasks > 0 && completedCount === totalTasks && proj.status !== 'archived') {
        newStatus = 'completed';
      } else if (completedCount > 0 && completedCount < totalTasks && (proj.status === 'planning' || proj.status === 'not-started')) {
        newStatus = 'active';
      } else if (completedCount === 0 && proj.status === 'completed') {
        newStatus = 'active';
      }
      const data = { name: proj.name, description: proj.description, status: newStatus, budget: proj.budget, startDate: proj.start_date || proj.startDate, endDate: proj.end_date || proj.endDate, propertyId: proj.property_id || proj.propertyId, tasks: updatedTasks };
      const r = await projectsAPI.update(proj.id, data);
      setProjects(prev => prev.map(p => p.id === proj.id ? r : p));
      setSelectedProject(r);
      if (newStatus === 'completed' && proj.status !== 'completed') {
        setTimeout(() => alert('🎉 All tasks complete! Project automatically marked as Completed.'), 300);
      }
      return r;
    } catch (err) { alert(err.error || 'Failed'); return null; }
    finally { setLoading(false); }
  };

  const openAddTask  = () => { setTaskForm(defaultTaskForm); setEditTask(null); setShowTaskModal(true); };
  const openEditTask = (t) => {
    setTaskForm({ name: t.name || '', description: t.description || '', dueDate: t.dueDate || '', duration: t.duration || '', dependsOn: Array.isArray(t.dependsOn) ? t.dependsOn : (t.dependsOn ? [t.dependsOn] : []), completed: t.completed || false, assignedTo: t.assignedTo || '' });
    setEditTask(t); setShowTaskModal(true);
  };

  const handleTaskSubmit = async (e) => {
    e.preventDefault();
    const proj  = liveProj();
    const tasks = proj?.tasks || [];
    const updatedTasks = editTask
      ? tasks.map(t => t.id === editTask.id ? { ...t, ...taskForm, duration: taskForm.duration ? parseInt(taskForm.duration) : null } : t)
      : [...tasks, { id: `task-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`, ...taskForm, duration: taskForm.duration ? parseInt(taskForm.duration) : null, completed: false, createdDate: new Date().toISOString(), assignedTo: taskForm.assignedTo || user?.username || '' }];
    const result = await saveProjectTasks(updatedTasks);
    if (result) setShowTaskModal(false);
  };

  const handleDeleteTask = async (taskId) => {
    const proj       = liveProj();
    const allTasks   = proj?.tasks || [];
    // Check if any other task depends on this one (match by id OR dbId)
    const dependents = allTasks.filter(t => {
      const deps = Array.isArray(t.dependsOn) ? t.dependsOn : (t.dependsOn ? [t.dependsOn] : []);
      return deps.some(dep => String(dep) === String(taskId));
    });
    if (dependents.length > 0) { alert(`Cannot delete: ${dependents.map(t => t.name).join(', ')} depend on this task.`); return; }
    if (!window.confirm('Delete this task?')) return;
    await saveProjectTasks(allTasks.filter(t => t.id !== taskId));
  };

  // ─── FIX: dependency resolution handles both string legacy IDs and integer DB IDs ───

  const buildTaskMap = (tasks) => {
    const map = {};
    for (const t of tasks) {
      // Index by legacy string id (e.g. "task-1234-abc")
      if (t.id)   map[String(t.id)]   = t;
      // Index by DB integer id as string (e.g. "54") — this is what dependsOn stores
      if (t.dbId) map[String(t.dbId)] = t;
    }
    return map;
  };

  const resolveDeps = (task, taskMap) => {
    const deps = Array.isArray(task.dependsOn) ? task.dependsOn : (task.dependsOn ? [task.dependsOn] : []);
    return deps.map(dep => taskMap[String(dep)]).filter(Boolean);
  };

  const handleToggleTask = async (taskId) => {
    const proj    = liveProj();
    const tasks   = proj?.tasks || [];
    const task    = tasks.find(t => t.id === taskId);
    if (!task) return;

    const taskMap  = buildTaskMap(tasks);
    const depTasks = resolveDeps(task, taskMap);

    // Blocking check — only applies when trying to COMPLETE a task
    if (!task.completed && depTasks.length > 0) {
      const blockers = depTasks.filter(t => !t.completed);
      if (blockers.length > 0) {
        alert(`Blocked by: ${blockers.map(t => t.name).join(', ')}`);
        return;
      }
    }
    await saveProjectTasks(tasks.map(t => t.id === taskId ? { ...t, completed: !t.completed } : t));
  };

  const handleAddToCalendar = async (task) => {
    if (!task.dueDate) { alert('Task needs a due date.'); return; }
    const proj = liveProj();
    try {
      const r = await calendarAPI.create({ id: `proj-${proj.id}-${task.id}`, date: task.dueDate, title: `${task.name} [${proj.name}]`, description: task.description || '', type: 'project', color: '#8b5cf6', completed: false });
      setCalendarTasks(prev => [r, ...prev]);
      alert('Added to calendar!');
    } catch (err) { alert(err.error || 'Failed'); }
  };

  const active   = projects.filter(p => p.status !== 'archived');
  const archived = projects.filter(p => p.status === 'archived');

  // ─── ARCHIVE VIEW ──────────────────────────────────────────────────────────

  if (showArchiveView) {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <button className="btn btn-secondary" onClick={() => setShowArchiveView(false)}>← Back to Projects</button>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700 }}>🗄️ Archived Projects ({archived.length})</h2><div />
        </div>
        {archived.length === 0 ? (
          <div className="card"><div className="empty-state"><span>🗄️</span><h3>No Archived Projects</h3></div></div>
        ) : (
          <div className="property-grid">
            {archived.map(proj => (
              <div key={proj.id} className="property-card" style={{ opacity: 0.8 }}>
                <div className="property-card-header">
                  <div style={{ fontWeight: 700 }}>{proj.name}</div>
                  <div style={{ fontSize: '0.75rem', opacity: 0.9 }}>{properties.find(p => p.id === (proj.property_id || proj.propertyId))?.address || 'General'}</div>
                </div>
                <div className="property-card-body">
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="btn btn-secondary" style={{ flex: 1, fontSize: '0.75rem' }} onClick={() => { setSelectedProject(proj); setShowArchiveView(false); }}>View</button>
                    <button className="btn btn-success" style={{ flex: 1, fontSize: '0.75rem' }} onClick={() => handleArchiveToggle(proj)}>📤 Restore</button>
                    <button className="btn btn-danger" style={{ fontSize: '0.75rem' }} onClick={() => handleDelete(proj.id)}>🗑️</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ─── PROJECT DETAIL ────────────────────────────────────────────────────────

  if (selectedProject) {
    const proj     = liveProj();
    if (!proj) { setSelectedProject(null); return null; }
    const tasks    = proj.tasks || [];
    const completed = tasks.filter(t => t.completed).length;
    const progress  = tasks.length > 0 ? (completed / tasks.length) * 100 : 0;
    const prop      = properties.find(p => p.id === (proj.property_id || proj.propertyId));
    const taskMap   = buildTaskMap(tasks);

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <button className="btn btn-secondary" onClick={() => setSelectedProject(null)}>← Back to Projects</button>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button className="btn btn-secondary" onClick={() => openEdit(proj)}>✏️ Edit</button>
            <button className="btn btn-secondary" style={{ background: '#f59e0b', color: 'white', border: 'none' }} onClick={() => saveAsTemplate(proj)}>📚 Save as Template</button>
            <button className="btn btn-secondary" style={{ background: '#64748b', color: 'white', border: 'none' }} onClick={() => handleArchiveToggle(proj)}>🗄️ Archive</button>
            <button className="btn btn-danger" onClick={() => handleDelete(proj.id)}>🗑️</button>
          </div>
        </div>

        <div className="card" style={{ marginBottom: '1.5rem', background: 'linear-gradient(135deg,var(--brand-green) 0%,var(--brand-green-light) 100%)', color: 'white' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}>{proj.name}</h2>
          <div style={{ display: 'flex', gap: '1.5rem', opacity: 0.9, fontSize: '0.875rem', flexWrap: 'wrap' }}>
            <span style={{ background: 'rgba(255,255,255,0.2)', padding: '0.1rem 0.5rem', borderRadius: '1rem' }}>{proj.status}</span>
            {prop && <span>🏠 {prop.address}</span>}
            {(proj.start_date || proj.startDate) && <span>📅 {formatDate(proj.start_date || proj.startDate)}</span>}
            {(proj.end_date || proj.endDate) && <span>🏁 {formatDate(proj.end_date || proj.endDate)}</span>}
            {proj.budget && <span>💰 Budget: {formatMoney(proj.budget, prop?.currency)}</span>}
          </div>
          {proj.description && <p style={{ marginTop: '0.5rem', opacity: 0.9, fontSize: '0.875rem' }}>{proj.description}</p>}
        </div>

        <div className="stats-grid" style={{ marginBottom: '1.5rem' }}>
          <div className="stat-card"><div className="stat-label">Total Tasks</div><div className="stat-value">{tasks.length}</div></div>
          <div className="stat-card"><div className="stat-label">Completed</div><div className="stat-value positive">{completed}</div></div>
          <div className="stat-card"><div className="stat-label">Remaining</div><div className="stat-value">{tasks.length - completed}</div></div>
          <div className="stat-card"><div className="stat-label">Progress</div><div className="stat-value">{progress.toFixed(0)}%</div><div style={{ background: '#e5e7eb', borderRadius: '9999px', height: '6px', marginTop: '0.5rem' }}><div style={{ width: `${progress}%`, background: 'var(--brand-green)', height: '6px', borderRadius: '9999px' }} /></div></div>
        </div>

        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <strong>📋 Tasks ({tasks.length})</strong>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn btn-secondary" style={{ fontSize: '0.8rem' }} onClick={async () => {
                const tasksWithDates = (proj?.tasks || []).filter(t => t.dueDate && !t.completed);
                if (!tasksWithDates.length) { alert('No tasks with due dates.'); return; }
                let added = 0;
                for (const task of tasksWithDates) {
                  try { const r = await calendarAPI.create({ id: `proj-${proj.id}-${task.id}`, date: task.dueDate, title: `${task.name} [${proj.name}]`, description: task.description || '', type: 'project', color: '#8b5cf6', completed: false }); setCalendarTasks(prev => [r, ...prev]); added++; } catch {}
                }
                alert(`Added ${added} tasks to calendar`);
              }}>📅 All to Calendar</button>
              <button className="btn btn-primary" onClick={openAddTask}>+ Add Task</button>
            </div>
          </div>

          {tasks.length === 0 ? (
            <div className="empty-state"><span>📋</span><h3>No Tasks Yet</h3><p>Add tasks to track progress</p></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {tasks.map(task => {
                const depTasks = resolveDeps(task, taskMap);
                const isBlocked = !task.completed && depTasks.some(d => !d.completed);
                return (
                  <div key={task.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '0.75rem', border: `1px solid ${isBlocked ? '#fbbf24' : task.completed ? '#e5e7eb' : 'var(--border-light)'}`, borderRadius: '0.5rem', background: task.completed ? 'var(--bg-light)' : isBlocked ? '#fffbeb' : 'white' }}>
                    <input type="checkbox" checked={task.completed} onChange={() => handleToggleTask(task.id)} disabled={isBlocked} style={{ marginTop: '3px', cursor: isBlocked ? 'not-allowed' : 'pointer', width: '16px', height: '16px' }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, textDecoration: task.completed ? 'line-through' : 'none', color: task.completed ? 'var(--text-secondary)' : isBlocked ? '#92400e' : 'inherit' }}>
                        {isBlocked && <span style={{ fontSize: '0.75rem', marginRight: '0.5rem' }}>🔒</span>}{task.name}
                      </div>
                      {task.description && <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{task.description}</div>}
                      <div style={{ display: 'flex', gap: '1rem', marginTop: '0.25rem', fontSize: '0.75rem', color: 'var(--text-secondary)', flexWrap: 'wrap' }}>
                        {task.dueDate && <span>📅 Due: {formatDate(task.dueDate)}</span>}
                        {task.duration && <span>⏱️ {task.duration} day{task.duration !== 1 ? 's' : ''}</span>}
                        {task.assignedTo && <span>👤 {task.assignedTo}</span>}
                        {depTasks.length > 0 && <span>🔗 Depends on: {depTasks.map(d => d.name).join(', ')}</span>}
                      </div>
                      {isBlocked && <div style={{ fontSize: '0.75rem', color: '#92400e', marginTop: '0.25rem' }}>⚠️ Blocked — complete dependencies first</div>}
                    </div>
                    <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
                      {task.dueDate && !task.completed && <button className="btn btn-secondary" style={{ padding: '0.2rem 0.4rem', fontSize: '0.7rem' }} onClick={() => handleAddToCalendar(task)}>📅</button>}
                      <button className="btn btn-secondary" style={{ padding: '0.2rem 0.4rem', fontSize: '0.7rem' }} onClick={() => openEditTask(task)}>✏️</button>
                      <button className="btn btn-danger" style={{ padding: '0.2rem 0.4rem', fontSize: '0.7rem' }} onClick={() => handleDeleteTask(task.id)}>×</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Task Modal */}
        {showTaskModal && (
          <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) e.preventDefault(); }} onClick={(e) => { if (e.target === e.currentTarget) setShowTaskModal(false); }}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header"><span className="modal-title">{editTask ? 'Edit Task' : 'Add Task'}</span><button className="modal-close" onClick={() => setShowTaskModal(false)}>×</button></div>
              <form onSubmit={handleTaskSubmit}>
                <div className="form-group"><label>Task Name *</label><input value={taskForm.name} onChange={e => setTF('name', e.target.value)} required placeholder="e.g. Submit mortgage application" /></div>
                <div className="form-group"><label>Description</label><textarea value={taskForm.description} onChange={e => setTF('description', e.target.value)} rows="2" /></div>
                <div className="form-grid">
                  <div className="form-group"><label>Duration (days)</label><input type="number" min="0" value={taskForm.duration} onChange={e => setTF('duration', e.target.value)} /></div>
                  <div className="form-group"><label>Due Date</label><input type="date" value={taskForm.dueDate} onChange={e => setTF('dueDate', e.target.value)} /></div>
                </div>
                {tasks.filter(t => !editTask || t.id !== editTask.id).length > 0 && (
                  <div className="form-group">
                    <label>Depends On (Optional)</label>
                    <div style={{ border: '1px solid var(--border-light)', borderRadius: '0.5rem', padding: '0.75rem', maxHeight: '160px', overflowY: 'auto' }}>
                      {tasks.filter(t => !editTask || t.id !== editTask.id).map(t => (
                        <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.375rem', cursor: 'pointer' }}>
                          <input type="checkbox" checked={(taskForm.dependsOn || []).some(dep => String(dep) === String(t.id) || (t.dbId && String(dep) === String(t.dbId)))} onChange={e => {
                            const deps = (taskForm.dependsOn || []).map(String);
                            // Store the legacy string id - server will resolve to DB id on save
                            setTF('dependsOn', e.target.checked ? [...deps, t.id] : deps.filter(id => id !== String(t.id) && id !== String(t.dbId)));
                          }} style={{ width: '16px', height: '16px' }} />
                          <span style={{ fontSize: '0.875rem' }}>{t.name}</span>{t.completed && <span style={{ fontSize: '0.7rem', color: 'var(--success)' }}>✅</span>}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                <div className="form-group">
                  <label>Assigned To</label>
                  {orgUsers.length > 1 ? (
                    <select value={taskForm.assignedTo} onChange={e => setTF('assignedTo', e.target.value)}>
                      <option value="">— Unassigned —</option>
                      {orgUsers.map(u => <option key={u.id || u.username} value={u.username}>{u.username}{u.username === user?.username ? ' (you)' : ''}</option>)}
                    </select>
                  ) : (
                    <input value={taskForm.assignedTo} onChange={e => setTF('assignedTo', e.target.value)} placeholder={user?.username || 'Enter username'} />
                  )}
                  <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>Default: {user?.username} (project owner)</small>
                </div>
                {editTask && <div className="form-group"><label>Completed?</label><select value={taskForm.completed} onChange={e => setTF('completed', e.target.value === 'true')}><option value="false">No</option><option value="true">Yes</option></select></div>}
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowTaskModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving...' : editTask ? '💾 Save' : '✅ Add Task'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Project Modal */}
        {showAddModal && (
          <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) e.preventDefault(); }} onClick={(e) => { if (e.target === e.currentTarget) setShowAddModal(false); }}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header"><span className="modal-title">✏️ Edit Project</span><button className="modal-close" onClick={() => setShowAddModal(false)}>×</button></div>
              {error && <div className="alert alert-danger">{error}</div>}
              <form onSubmit={handleSubmit}>
                <div className="form-group"><label>Project Name *</label><input value={form.name} onChange={e => set('name', e.target.value)} required /></div>
                <div className="form-grid">
                  <div className="form-group"><label>Status</label><select value={form.status} onChange={e => set('status', e.target.value)}>{STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}</select></div>
                  <div className="form-group"><label>Budget ({symbolFor((properties.find(p => String(p.id) === String(form.propertyId)) || {}).currency)})</label><input type="number" step="0.01" value={form.budget} onChange={e => set('budget', e.target.value)} placeholder="0.00" /></div>
                  <div className="form-group"><label>Start Date</label><input type="date" value={form.startDate} onChange={e => set('startDate', e.target.value)} /></div>
                  <div className="form-group"><label>Target End Date</label><input type="date" value={form.endDate} onChange={e => set('endDate', e.target.value)} /></div>
                </div>
                <div className="form-group"><label>Property</label><select value={form.propertyId || ''} onChange={e => set('propertyId', e.target.value || null)}><option value="">General</option>{properties.map(p => <option key={p.id} value={p.id}>{p.address}</option>)}</select></div>
                <div className="form-group"><label>Description</label><textarea value={form.description} onChange={e => set('description', e.target.value)} rows="3" /></div>
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving...' : '💾 Save Changes'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─── LIST VIEW ─────────────────────────────────────────────────────────────

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left"><h1 className="page-title">Projects</h1></div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          {archived.length > 0 && <button className="btn btn-secondary" onClick={() => setShowArchiveView(true)}>🗄️ Archive ({archived.length})</button>}
          <button className="btn btn-secondary" onClick={() => setShowTemplatesModal(true)}>📚 Templates {templates.length > 0 ? `(${templates.length})` : ''}</button>
          <button className="btn btn-primary" onClick={openAdd}>+ New Project</button>
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: '1.5rem' }}>
        <div className="stat-card"><div className="stat-label">Active</div><div className="stat-value positive">{active.filter(p => p.status === 'active').length}</div></div>
        <div className="stat-card"><div className="stat-label">Planning</div><div className="stat-value">{active.filter(p => p.status === 'planning' || p.status === 'not-started').length}</div></div>
        <div className="stat-card"><div className="stat-label">Completed</div><div className="stat-value">{active.filter(p => p.status === 'completed').length}</div></div>
        <div className="stat-card"><div className="stat-label">Archived</div><div className="stat-value" style={{ color: 'var(--text-secondary)' }}>{archived.length}</div></div>
      </div>

      {active.length === 0 ? (
        <div className="card"><div className="empty-state"><span>🔨</span><h3>No Active Projects</h3><p>Create a project to track maintenance and renovations</p><button className="btn btn-primary" onClick={openAdd}>Add First Project</button></div></div>
      ) : (
        <div className="property-grid">
          {active.map(proj => {
            const prop  = properties.find(p => p.id === (proj.property_id || proj.propertyId));
            const tasks = proj.tasks || [];
            const done  = tasks.filter(t => t.completed).length;
            const prog  = tasks.length > 0 ? (done / tasks.length) * 100 : 0;
            return (
              <div key={proj.id} className="property-card" onClick={() => setSelectedProject(proj)}>
                <div className="property-card-header">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div><div style={{ fontWeight: 700, fontSize: '1rem' }}>{proj.name}</div><div style={{ fontSize: '0.75rem', opacity: 0.9 }}>{prop?.address || 'General'}</div></div>
                    <span style={{ background: 'rgba(255,255,255,0.2)', padding: '0.2rem 0.5rem', borderRadius: '1rem', fontSize: '0.7rem', fontWeight: 600 }}>{proj.status}</span>
                  </div>
                </div>
                <div className="property-card-body">
                  {proj.description && <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>{proj.description}</div>}
                  {proj.budget && <div style={{ fontSize: '0.8rem', marginBottom: '0.5rem' }}><span style={{ color: 'var(--text-secondary)' }}>Budget: </span><strong>{formatMoney(proj.budget, prop?.currency)}</strong></div>}
                  {tasks.length > 0 && (
                    <div style={{ marginBottom: '0.5rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: '0.25rem' }}><span style={{ color: 'var(--text-secondary)' }}>Tasks:</span><strong>{done}/{tasks.length}</strong></div>
                      <div style={{ background: '#e5e7eb', borderRadius: '9999px', height: '5px' }}><div style={{ width: `${prog}%`, background: 'var(--brand-green)', height: '5px', borderRadius: '9999px' }} /></div>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }} onClick={e => e.stopPropagation()}>
                    <button className="btn btn-secondary" style={{ flex: 1, padding: '0.3rem', fontSize: '0.75rem' }} onClick={() => setSelectedProject(proj)}>View</button>
                    <button className="btn btn-secondary" style={{ padding: '0.3rem 0.5rem', fontSize: '0.75rem' }} onClick={() => openEdit(proj)}>✏️</button>
                    <button className="btn btn-secondary" style={{ padding: '0.3rem 0.4rem', fontSize: '0.75rem', background: '#64748b', color: 'white', border: 'none' }} title="Archive" onClick={() => handleArchiveToggle(proj)}>🗄️</button>
                    <button className="btn btn-danger" style={{ padding: '0.3rem 0.5rem', fontSize: '0.75rem' }} onClick={() => handleDelete(proj.id)}>🗑️</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* New Project Modal */}
      {showAddModal && !editProject && (
        <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) e.preventDefault(); }} onClick={(e) => { if (e.target === e.currentTarget) setShowAddModal(false); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><span className="modal-title">🔨 New Project</span><button className="modal-close" onClick={() => setShowAddModal(false)}>×</button></div>
            {error && <div className="alert alert-danger">{error}</div>}
            <form onSubmit={handleSubmit}>
              {templates.length > 0 && (
                <div className="form-group" style={{ background: '#eff6ff', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid #bfdbfe' }}>
                  <label style={{ color: '#1e40af', fontWeight: 600 }}>📚 Start from Template (Optional)</label>
                  <select value={selectedTemplate} onChange={e => { setSelectedTemplate(e.target.value); if (e.target.value) applyTemplate(e.target.value); }}>
                    <option value="">— Start from scratch —</option>
                    {templates.map(t => <option key={t.id} value={t.id}>{t.name} ({t.tasks.length} tasks)</option>)}
                  </select>
                </div>
              )}
              <div className="form-group"><label>Project Name *</label><input value={form.name} onChange={e => set('name', e.target.value)} required placeholder="e.g. Kitchen Refurbishment" /></div>
              <div className="form-grid">
                <div className="form-group"><label>Status</label><select value={form.status} onChange={e => set('status', e.target.value)}>{STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}</select></div>
                <div className="form-group"><label>Budget ({symbolFor((properties.find(p => String(p.id) === String(form.propertyId)) || {}).currency)})</label><input type="number" step="0.01" value={form.budget} onChange={e => set('budget', e.target.value)} placeholder="0.00" /></div>
                <div className="form-group"><label>Start Date</label><input type="date" value={form.startDate} onChange={e => set('startDate', e.target.value)} /></div>
                <div className="form-group"><label>Target End Date</label><input type="date" value={form.endDate} onChange={e => set('endDate', e.target.value)} /></div>
              </div>
              <div className="form-group"><label>Property</label><select value={form.propertyId || ''} onChange={e => set('propertyId', e.target.value || null)}><option value="">General (No specific property)</option>{properties.map(p => <option key={p.id} value={p.id}>{p.address}</option>)}</select></div>
              <div className="form-group"><label>Description</label><textarea value={form.description} onChange={e => set('description', e.target.value)} rows="3" placeholder="Project details..." /></div>
              {form.tasks.length > 0 && <div style={{ background: '#f0fdf4', padding: '0.75rem', borderRadius: '0.5rem', marginBottom: '1rem', fontSize: '0.875rem', color: '#15803d' }}>✅ Template applied — {form.tasks.length} task{form.tasks.length !== 1 ? 's' : ''} pre-loaded</div>}
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving...' : '✅ Create Project'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Templates Modal */}
      {showTemplatesModal && (
        <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) e.preventDefault(); }} onClick={(e) => { if (e.target === e.currentTarget) setShowTemplatesModal(false); }}>
          <div className="modal" style={{ maxWidth: '680px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header"><span className="modal-title">📚 Project Templates</span><button className="modal-close" onClick={() => setShowTemplatesModal(false)}>×</button></div>
            {templates.length === 0 ? (
              <div className="empty-state" style={{ padding: '2rem' }}>
                <span>📚</span><h3>No Templates Yet</h3>
                <p>Open a project and click <strong>"Save as Template"</strong> to create reusable project templates.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {templates.map(tmpl => (
                  <div key={tmpl.id} style={{ border: '1px solid var(--border-light)', borderRadius: '0.5rem', padding: '1.25rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                      <div>
                        <strong style={{ fontSize: '1rem' }}>📚 {tmpl.name}</strong>
                        {tmpl.description && <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: '0.25rem 0 0' }}>{tmpl.description}</p>}
                      </div>
                      <button className="btn btn-danger" style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }} onClick={() => deleteTemplate(tmpl.id)}>Delete</button>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
                      <strong>{tmpl.tasks.length}</strong> task{tmpl.tasks.length !== 1 ? 's' : ''}
                      {tmpl.tasks.slice(0, 4).map((t, i) => <div key={i} style={{ marginTop: '0.2rem' }}>• {t.name}</div>)}
                      {tmpl.tasks.length > 4 && <div style={{ fontStyle: 'italic' }}>...and {tmpl.tasks.length - 4} more</div>}
                    </div>
                    <button className="btn btn-primary" style={{ fontSize: '0.8rem' }} onClick={() => { setShowTemplatesModal(false); setForm(p => ({ ...p, tasks: tmpl.tasks.map(t => ({ ...t, id: `task_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`, completed: false })), name: p.name || tmpl.name, description: p.description || tmpl.description })); setShowAddModal(true); }}>
                      ✅ Use this Template
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
              <button className="btn btn-secondary" onClick={() => setShowTemplatesModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
